import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import heroImage from './assets/photos/IMG_8891.jpg';
import render1 from './assets/renders/3_Legged_Stool_v1_2025-Jul-30_06-59-36AM-000_CustomizedView193487461_png.png';
import render2 from './assets/renders/3_Legged_Stool_v1_2025-Jul-30_07-11-22AM-000_CustomizedView20930276724_png.png';
import design1 from './assets/designs/laidback/LAIDBACK_v1_2025-Aug-02_07-42-56AM-000_CustomizedView13881599587.png';
import design2 from './assets/designs/laidback/LAIDBACK_v1_2025-Aug-02_07-25-38AM-000_CustomizedView16209689747.png';
import logo from './assets/logo/logo-graphics.svg';



export default function App() {
  const [fadeIn, setFadeIn] = useState(false);
  const [animatedElements, setAnimatedElements] = useState(new Set());
  const [navScrolled, setNavScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);


  useEffect(() => {
    setFadeIn(true);
    
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: [0.1, 0.3, 0.5], // Multiple thresholds for better mobile detection
      rootMargin: '0px 0px -20px 0px' // Smaller margin for mobile
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setAnimatedElements(prev => new Set(prev).add(entry.target.dataset.animateId));
        }
      });
    }, observerOptions);

    // Observe elements for animation
    const animateElements = document.querySelectorAll('[data-animate]');
    animateElements.forEach(el => {
      el.dataset.animateId = Math.random().toString(36).substr(2, 9);
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Navigation scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setNavScrolled(scrolled);
      
      // Calculate scroll progress
      const scrollProgress = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      setScrollProgress(Math.min(scrollProgress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mobile-specific animation triggers
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // Trigger animations on mobile with a slight delay
      const timer = setTimeout(() => {
        const animateElements = document.querySelectorAll('[data-animate]');
        animateElements.forEach((el, index) => {
          setTimeout(() => {
            setAnimatedElements(prev => new Set(prev).add(el.dataset.animateId || `mobile-${index}`));
          }, index * 200); // Stagger animations on mobile
        });
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Rotating hero background images
  useEffect(() => {
    const heroImages = [heroImage, render1, render2, design1, design2];
    const interval = setInterval(() => {
      setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 3500); // 3 seconds per image + 0.5 seconds transition

    return () => clearInterval(interval);
  }, []);



  const handleContact = () => {
    window.open('mailto:info@escapestud.io');
  };

  const handleLocation = () => {
    window.open('https://maps.google.com/?q=San+José,+Costa+Rica');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };



  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          margin: 0;
          padding: 0;
          font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
        }
        html {
          margin: 0;
          padding: 0;
          scroll-behavior: smooth;
        }
        
        /* Force Apple-style font across all elements */
        *, *::before, *::after {
          font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
        }
        
        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Safari and Instagram browser compatibility */
        @-webkit-keyframes fadeInUp {
          from {
            opacity: 0;
            -webkit-transform: translateY(30px);
          }
          to {
            opacity: 1;
            -webkit-transform: translateY(0);
          }
        }
        
        .fade-in-up {
          animation: fadeInUp 0.6s ease-out;
        }
        
        /* Mobile-specific animations */
        @media (max-width: 768px) {
          .fade-in-up {
            animation: fadeInUp 0.4s ease-out;
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        }
        
        /* Navigation animations */
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        /* Safari navigation animations */
        @-webkit-keyframes slideDown {
          from {
            opacity: 0;
            -webkit-transform: translateY(-100%);
          }
          to {
            opacity: 1;
            -webkit-transform: translateY(0);
          }
        }
        
        .nav-slide-down {
          animation: slideDown 0.5s ease-out;
        }
        
        .nav-scrolled {
          background-color: rgba(26, 26, 26, 0.95) !important;
          -webkit-backdrop-filter: blur(10px);
          backdrop-filter: blur(10px);
          box-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
        }
        
        /* Custom scrollbar - matches top progress bar */
        html::-webkit-scrollbar {
          width: 6px;
        }
        
        html::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        
        html::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #ff6b35, #ff5722);
          border-radius: 3px;
        }
        
        html::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #ff5722, #ff6b35);
        }
        
        body::-webkit-scrollbar {
          width: 6px;
        }
        
        body::-webkit-scrollbar-track {
          background: #1a1a1a;
        }
        
        body::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, #ff6b35, #ff5722);
          border-radius: 3px;
        }
        
        body::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(180deg, #ff5722, #ff6b35);
        }
        
        /* Mobile scrollbar support - matches top progress bar */
        @media (max-width: 768px) {
          html::-webkit-scrollbar {
            width: 4px;
          }
          
          html::-webkit-scrollbar-track {
            background: #1a1a1a;
          }
          
          html::-webkit-scrollbar-thumb {
            background: #ff6b35 !important;
            border-radius: 2px;
          }
          
          html::-webkit-scrollbar-thumb:hover {
            background: #ff5722 !important;
          }
          
          body::-webkit-scrollbar {
            width: 4px;
          }
          
          body::-webkit-scrollbar-track {
            background: #1a1a1a;
          }
          
          body::-webkit-scrollbar-thumb {
            background: #ff6b35 !important;
            border-radius: 2px;
          }
          
          body::-webkit-scrollbar-thumb:hover {
            background: #ff5722 !important;
          }
          
          /* Force mobile scrollbar colors */
          *::-webkit-scrollbar {
            width: 4px;
          }
          
          *::-webkit-scrollbar-track {
            background: #1a1a1a;
          }
          
          *::-webkit-scrollbar-thumb {
            background: #ff6b35 !important;
            border-radius: 2px;
          }
          
          *::-webkit-scrollbar-thumb:hover {
            background: #ff5722 !important;
          }
        }
        
        /* Firefox scrollbar - matches top progress bar */
        * {
          scrollbar-width: thin;
          scrollbar-color: #ff6b35 #1a1a1a;
        }
        
        /* Mobile Firefox scrollbar */
        @media (max-width: 768px) {
          * {
            scrollbar-width: thin;
            scrollbar-color: #ff6b35 #1a1a1a;
          }
        }
        
        /* Mobile touch scrolling improvements */
        @media (hover: none) and (pointer: coarse) {
          html {
            -webkit-overflow-scrolling: touch;
            scroll-behavior: smooth;
          }
          
          body {
            -webkit-overflow-scrolling: touch;
          }
        }
        
        /* Instagram browser and iOS Safari specific fixes */
        @supports (-webkit-touch-callout: none) {
          /* iOS Safari and Instagram browser */
          html, body {
            -webkit-overflow-scrolling: touch;
            -webkit-text-size-adjust: 100%;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
          }
          
          /* Fix for Instagram browser viewport issues */
          @media screen and (max-width: 768px) {
            html, body {
              width: 100%;
              min-height: 100vh;
              overflow-x: hidden;
            }
          }
        }
        
        /* Safari-specific scrollbar fixes */
        @media screen and (-webkit-min-device-pixel-ratio: 0) {
          ::-webkit-scrollbar {
            -webkit-appearance: none;
          }
          
          ::-webkit-scrollbar-thumb {
            border-radius: 3px;
            background-color: #ff6b35;
            -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
          }
        }
        
        /* Mobile hero background positioning */
        @media (max-width: 768px) {
          #hero {
            background-position: center 20% !important;
          }
          
          /* Mobile hero logo styling */
          #hero img {
            height: 50px !important;
            width: auto !important;
            margin-bottom: 16px !important;
          }
          
          #hero p {
            font-size: 14px !important;
            margin-bottom: 24px !important;
          }
          
          #hero > div > div {
            left: 50% !important;
            top: 35vh !important;
            transform: translateX(-50%) !important;
            max-width: calc(100% - 40px) !important;
            text-align: center !important;
          }
          
          #hero button {
            padding: 10px 20px !important;
            font-size: 12px !important;
          }
        }
        
        /* Tablet adjustments */
        @media (max-width: 1024px) and (min-width: 769px) {
          #hero > div > div {
            left: 70px !important;
            max-width: calc(100% - 100px) !important;
          }
        }
        
        /* Mobile navigation styles */
        @media (max-width: 768px) {
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .navigation-bar {
            display: block !important;
          }
        }
        
        @media (min-width: 769px) {
          .navigation-bar {
            display: none !important;
          }
          .mobile-menu-btn {
            display: none !important;
          }
          .mobile-menu {
            display: none !important;
          }
        }
      `}</style>
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#1a1a1a',
        color: 'white',
                  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important",
        margin: 0,
        padding: 0,
        width: '100%',
        boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch',
        WebkitTextSizeAdjust: '100%',
        WebkitFontSmoothing: 'antialiased'
      }}>
        {/* Scroll Progress Indicator */}
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: `${scrollProgress}%`,
          height: '3px',
          background: 'linear-gradient(90deg, #ff6b35, #ff5722)',
          zIndex: 10001,
          transition: 'width 0.3s ease'
        }} />
      {/* Navigation */}
      <nav 
        className={`nav-slide-down navigation-bar ${navScrolled ? 'nav-scrolled' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          width: '100%',
          backgroundColor: navScrolled ? 'rgba(26, 26, 26, 0.2)' : 'rgba(26, 26, 26, 0.2)',
          zIndex: 1000,
          padding: '10px 0',
          borderBottom: navScrolled ? '1px solid #333' : 'none',
          WebkitBackdropFilter: 'blur(10px)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          WebkitTransition: 'all 0.3s ease',
          boxShadow: navScrolled ? '0 2px 20px rgba(0, 0, 0, 0.3)' : 'none',
          display: 'none'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '0 20px',
          maxWidth: 1200,
          margin: '0 auto',
          position: 'relative'
        }}>

          
          {/* Desktop Navigation */}
          <div className="desktop-nav" style={{ display: 'flex', gap: 20 }}>
            <div 
              onClick={() => scrollToSection('hero')}
              style={{ padding: '5px 0', cursor: 'pointer' }}
            >
              Home
            </div>

            <div 
              onClick={() => scrollToSection('contact')}
              style={{ padding: '5px 0', cursor: 'pointer' }}
            >
              Contact
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <div 
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ 
              display: 'none', 
              cursor: 'pointer',
              padding: '5px',
              position: 'absolute',
              right: '20px'
            }}
          >
            <div style={{
              width: 25,
              height: 3,
              backgroundColor: 'white',
              margin: '5px 0',
              transition: 'all 0.3s ease',
              transform: mobileMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
            }} />
            <div style={{
              width: 25,
              height: 3,
              backgroundColor: 'white',
              margin: '5px 0',
              transition: 'all 0.3s ease',
              opacity: mobileMenuOpen ? 0 : 1
            }} />
            <div style={{
              width: 25,
              height: 3,
              backgroundColor: 'white',
              margin: '5px 0',
              transition: 'all 0.3s ease',
              transform: mobileMenuOpen ? 'rotate(-45deg) translate(7px, -6px)' : 'none'
            }} />
          </div>
        </div>
        
        {/* Mobile Menu */}
        <div 
          className="mobile-menu"
          style={{
            display: mobileMenuOpen ? 'block' : 'none',
            backgroundColor: 'rgba(26, 26, 26, 0.98)',
            borderTop: '1px solid #333',
            padding: '20px'
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 15
          }}>
            <div 
              onClick={() => {
                scrollToSection('hero');
                setMobileMenuOpen(false);
              }}
              style={{ 
                padding: '15px 0', 
                cursor: 'pointer',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                fontSize: 18,
                fontWeight: '500'
              }}
            >
              Home
            </div>

            <div 
              onClick={() => {
                scrollToSection('contact');
                setMobileMenuOpen(false);
              }}
              style={{ 
                padding: '15px 0', 
                cursor: 'pointer',
                fontSize: 18,
                fontWeight: '500'
              }}
            >
              Contact
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section 
        id="hero"
        data-animate="true"
        style={{
        height: '100vh',
        position: 'relative',
        backgroundColor: '#2a2a2a',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        margin: 0,
        padding: 0,
        boxSizing: 'border-box',
                  backgroundImage: `url(${[heroImage, render1, render2, design1, design2][currentHeroImage]})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background-image 0.5s ease-in-out'
      }}>
        <div style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.4)'
        }} />
        <div 
          data-animate="true"
          style={{
            position: 'absolute',
            zIndex: 1,
            right: '50px',
            bottom: '50px',
            left: 'auto',
            top: 'auto',
            maxWidth: 'auto',
            opacity: fadeIn ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            textAlign: 'right'
          }}
        >
                      <img 
                        src={logo} 
                        alt="escape studio" 
                        style={{
                          height: 84,
                          width: 'auto',
                          marginBottom: 0,
                          filter: 'brightness(0.8) saturate(0.8)',
                          objectFit: 'contain'
                        }}
                      />
        </div>
        
        {/* Centered text */}
        <div 
          style={{
            position: 'absolute',
            zIndex: 1,
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            opacity: fadeIn ? 1 : 0,
            transition: 'opacity 1s ease-in-out'
          }}
        >
          <h1 style={{
            fontSize: 21,
            fontWeight: '300',
            color: 'rgba(255, 255, 255, 0.85)',
            margin: 0,
            textShadow: 'none',
            letterSpacing: '1px',
            fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"
          }}>
            escape studio
          </h1>
        </div>
      </section>



      {/* Contact Section */}
      <section 
        id="contact"
        data-animate="true"
        className={animatedElements.has(document.querySelector('[data-animate]')?.dataset.animateId) ? 'fade-in-up' : ''}
        style={{
          padding: '80px 0',
          backgroundColor: '#2a2a2a',
          width: '100%',
          margin: 0,
          boxSizing: 'border-box',
          opacity: animatedElements.has(document.querySelector('[data-animate]')?.dataset.animateId) ? 1 : 0,
          transform: animatedElements.has(document.querySelector('[data-animate]')?.dataset.animateId) ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 20px'
        }}>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 40, flexWrap: 'wrap' }}>
            <div 
              onClick={handleContact}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 15,
                padding: '30px 20px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{
                fontSize: 32,
                marginBottom: 10
              }}>
                ✉️
              </div>
              <div>
                <h3 style={{
                  fontSize: 14,
                  fontWeight: '500',
                  marginBottom: 8,
                  color: '#e8e8e8'
                }}>
                  Email
                </h3>
                <p style={{ fontSize: 11, color: '#d0d0d0' }}>
                  info@escapestud.io
                </p>
              </div>
            </div>
            <div 
              onClick={handleLocation}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 15,
                padding: '30px 20px',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              <div style={{
                fontSize: 32,
                marginBottom: 10
              }}>
                📍
              </div>
              <div>
                <h3 style={{
                  fontSize: 14,
                  fontWeight: '500',
                  marginBottom: 8,
                  color: '#e8e8e8'
                }}>
                  Location
                </h3>
                <p style={{ fontSize: 11, color: '#d0d0d0' }}>
                  San José, Costa Rica
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer 
        data-animate="true"
        style={{
          backgroundColor: '#1a1a1a',
          padding: '60px 0',
          borderTop: '1px solid #333',
          width: '100%',
          margin: 0,
          boxSizing: 'border-box',
          opacity: animatedElements.has(document.querySelector('[data-animate]')?.dataset.animateId) ? 1 : 0,
          transform: animatedElements.has(document.querySelector('[data-animate]')?.dataset.animateId) ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 40,
            flexWrap: 'wrap',
            gap: 40
          }}>
            <div style={{ flex: 1, minWidth: 250 }}>
              <h3 style={{
                fontSize: 18,
                fontWeight: '700',
                color: '#e8e8e8',
                marginBottom: 15
              }}>
                escapestud.io
              </h3>
              <p style={{
                fontSize: 12,
                color: '#d0d0d0',
                marginBottom: 20
              }}>
                Dissapear into space and sound.
              </p>
              <div style={{ display: 'flex', gap: 15 }}>
                <div style={{
                  width: 40,
                  height: 40,
                  backgroundColor: '#333',
                  borderRadius: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  cursor: 'pointer'
                }}>
                  📷
                </div>
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 200 }}>
              <h4 style={{
                fontSize: 14,
                fontWeight: '600',
                marginBottom: 15,
                color: '#e8e8e8'
              }}>
                Company
              </h4>
              <div 
                onClick={handleContact}
                style={{
                  fontSize: 12,
                  color: '#d0d0d0',
                  cursor: 'pointer'
                }}
              >
                Contact
              </div>
            </div>
          </div>
          <div style={{
            borderTop: '1px solid #333',
            paddingTop: 20,
            textAlign: 'center'
          }}>
            <p style={{ fontSize: 11, color: '#999' }}>
              &copy; 2025 Escape Studio. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      </div>
    </>
  );
}

// Mount the app to the DOM using React 18 API
const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}
